/**
 * TypeScript types for the ZTNA admin dashboard + wizard.
 *
 * These types mirror the Pydantic schemas in
 * `src/consoleai/ztna_onboarding/schemas.py` and `models.py`. Keep them in
 * sync — the backend uses ConfigDict(extra='forbid') so unknown keys break.
 */

// ─────────────────────────────────────────────────────────────────
// Job lifecycle
// ─────────────────────────────────────────────────────────────────

export type JobStatus =
  | 'pending'
  // Espera aprovacao de GMUD; o worker NAO reivindica job neste estado, por
  // desenho. Nao e' progresso nem terminal — o scheduler o promove a `pending`
  // quando a GMUD e' aprovada, ou o cancela se ela for rejeitada.
  | 'pending_gmud'
  | 'running'
  | 'success'
  | 'failed'
  | 'needs_validation'
  | 'cancelling'
  | 'cancelled'

export type Environment = 'DEV' | 'QA' | 'HML' | 'PRD'
export type Protocol = 'http' | 'ssh' | 'tcp'
export type RoutingMode = 'direct' | 'connector'
export type IdentityMode = 'header' | 'oidc' | 'mtls'

/** Janela de horário permitido (HH:MM 24h). Mirrors ztna.models.TimeWindow. */
export interface TimeWindow {
  start: string
  end: string
  tz: string
  weekdays_only: boolean
  /** 'app' = janela usa tz fixo. 'user' = janela usa fuso do usuario. */
  tz_source?: 'app' | 'user'
}

/** Wizard submission contract. Mirrors ztna_onboarding.models.JobPayload. */
export interface JobPayload {
  tenant_id: number
  zone_id: string
  record_name: string
  public_hostname: string
  slug: string
  display_name: string
  environment: Environment
  canonical_hostname: string
  protocol: Protocol
  routing_mode: RoutingMode
  identity_mode: IdentityMode
  upstream_url: string
  required_permissions: string[]
  oidc_redirect_uris: string[]
  /** Gap 3 (2026-05-10): visibilidade org-default. Default true = todos membros do tenant veem. */
  org_default_visible: boolean
  /** Quando true, apenas funcionários da organização dona do app acessam. Default false = público. */
  is_private?: boolean
  /** Restrição de horário (opcional). Quando null/undefined, sem restrição. */
  allowed_hours?: TimeWindow | null
  /** Roles que ignoram allowed_hours. null = usar default backend (admin/manager/gestor). */
  bypass_time_window_roles?: string[] | null
}

export interface JobProgress {
  step: string
  percent: number
  sub_progress?: Record<string, unknown> | null
}

export interface CompletedStep {
  step: string
  resource_id: string | null
  resource_arn: string | null
  action: 'created' | 'reused'
  completed_at: string
}

export interface JobResponse {
  id: number
  status: JobStatus
  user_id: number
  tenant_id: number
  application_id: number | null
  payload: Record<string, unknown>
  progress: Record<string, unknown>
  completed_steps: CompletedStep[]
  in_flight_step: string | null
  attempt_seq: number
  error: string | null
  error_details: Record<string, unknown> | null
  smoke_attempts?: unknown | null
  parent_job_id: number | null
  /**
   * Preenchido quando a acao foi para aprovacao em vez de aplicada.
   * A acao NAO aconteceu — os demais campos refletem o estado ATUAL, nao
   * o solicitado. Espelha o campo homonimo do schema do backend.
   */
  gmud_id?: number | null
  created_at: string
  started_at: string | null
  finished_at: string | null
}

export interface JobListResponse {
  items: JobResponse[]
  total: number
}

export interface JobSubmitResponse {
  job_id: number
  status: JobStatus | string
  duplicate_of?: number | null
  /**
   * Preenchido quando a acao foi para aprovacao em vez de aplicada.
   * A acao NAO aconteceu — os demais campos refletem o estado ATUAL, nao
   * o solicitado. Espelha o campo homonimo do schema do backend.
   */
  gmud_id?: number | null
}

// ─────────────────────────────────────────────────────────────────
// Drafts (used by Lote 7 Wizard)
// ─────────────────────────────────────────────────────────────────

export interface DraftResponse {
  id: number
  user_id: number
  tenant_id: number | null
  current_step: number
  step_data: Record<string, unknown>
  created_at: string
  updated_at: string
  submitted_at: string | null
  job_id: number | null
}

// ─────────────────────────────────────────────────────────────────
// Discovery
// ─────────────────────────────────────────────────────────────────

export interface TenantDiscoveryItem {
  id: number
  name: string
  environment: string
  account_id: string | null
  /**
   * Sub-projeto 4 Lote 1 — preenchido após bootstrap. Quando null, o
   * wizard renderiza ``TenantPrepareBanner`` antes de listar zonas.
   */
  ztna_provision_role_arn?: string | null
}

/**
 * Sub-projeto 4 Lote 1 — response de POST /tenants/{id}/prepare.
 */
export interface TenantPrepareResponse {
  tenant_id: number
  role_arn: string
  already_prepared: boolean
  /** 'created' | 'trust_repaired' | 'verified' — o que o backend fez de fato. */
  action?: 'created' | 'trust_repaired' | 'verified'
  /** O assume foi conferido AGORA e passou. `false` = escrita feita, acesso nao provado. */
  verified?: boolean
  verification_error?: string | null
  message?: string | null
}

export interface HostedZoneItem {
  id: string
  name: string
  private: boolean
  record_count: number | null
}

export interface HostedZoneRecordItem {
  name: string
  type: string
  ttl: number | null
  values: string[]
}

/**
 * Resultado do GET /route53/dns-lookup — detecta DNS legacy pra hostname
 * dado e propoe upstream_url. Frontend usa pra auto-fill em Step 3.
 *
 * Quando ``record_type='NONE'``, nao ha conflito (apps novos sem registro
 * pre-existente); demais campos sao null. Wizard segue fluxo normal.
 */
export interface DnsLookupResponse {
  record_type: 'A_ALIAS' | 'A_DIRECT' | 'CNAME' | 'NONE'
  target: string | null
  target_kind: 'aws_elb' | 'aws_ec2_ip' | 'external_host' | 'unknown' | null
  proposed_upstream_url: string | null
  proposed_protocol: 'https' | 'http'
}

// ─────────────────────────────────────────────────────────────────
// Pre-flight (used by Lote 7 Wizard)
// ─────────────────────────────────────────────────────────────────

export type PreflightStatus = 'ok' | 'warn' | 'fail'

/**
 * Sub-projeto 4 Lote 2 — DNS conflict detection.
 * Carregado em ``PreflightCheckResult.conflict`` quando check_name === 'dns_conflict'.
 */
export interface LegacyDnsConflict {
  record_type: 'A_ALIAS' | 'A_DIRECT' | 'CNAME' | 'NONE'
  target: string | null
  target_kind: 'aws_elb' | 'aws_ec2_ip' | 'external_host' | 'unknown' | null
  proposed_upstream_url: string | null
  proposed_protocol: 'https' | 'http'
  record_set: Record<string, unknown> | null
  record_checksum: string | null
  /** Política de roteamento (weighted, latency...) ou null se simples. Preenchida → o check reprova. */
  routing_policy?: string | null
}

export interface PreflightCheckResult {
  name: string
  status: PreflightStatus
  detail: string
  /** Apenas check 'dns_conflict' (Sub-projeto 4 Lote 2). */
  conflict?: LegacyDnsConflict
}

export interface PreflightResponse {
  checks: PreflightCheckResult[]
  can_submit: boolean
}

// ─────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────

export interface DashboardKpis {
  apps_total: number
  healthy_count: number
  accesses_24h: number
  certs_expiring_30d: number
  /** Apps com enabled=false (suspensas). Click no card liga filtro 'Mostrar suspensas'. */
  apps_disabled?: number
}

export interface AppHealthSummary {
  id: number
  slug: string
  hostname: string
  display_name: string
  tenant_id: number
  environment: string
  routing_mode: string
  identity_mode: string
  upstream_url: string | null
  oidc_redirect_uris: string[] | null
  access_policy: Record<string, unknown> | null
  enabled: boolean
  access_mode: 'enforced' | 'paused' | 'open'
  auth_bypass?: boolean
  last_probed_at: string | null
  last_ok: boolean | null
  last_http_status: number | null
  last_latency_ms: number | null
  cert_expires_at: string | null
  smoke_intercepted_by_broker: boolean | null
  /** Para onde o hostname aponta: `plataforma`, `fora`, ou `null` = NÃO MEDIDO.
   *  `null` não é "ok" — a tela diz "não medido" em texto, porque ícone apagado
   *  o olho lê como ausência de problema. */
  dns_target_check: 'plataforma' | 'fora' | null
  dns_target_checked_at: string | null
  /** A app tem provision success? Sem histórico ela não responde aos verbos
   *  normais de ciclo de vida — é o caso da adoção. */
  tem_historico: boolean
  /** Descartada do catálogo. Não volta pela adoção. */
  discarded_at: string | null
  open_mechanism: 'passthrough' | 'dns_only' | null
  /** Quando true, apenas funcionários da organização dona do app acessam. Default false = público. */
  is_private?: boolean
}

export interface AccessStatsTopUser {
  user_id: number | null
  user_email: string | null
  accesses: number
}

export interface AccessStatsDeny {
  reason: string
  count: number
}

export interface AccessStatsTopApp {
  application_id: number
  slug: string
  display_name: string
  tenant_id: number
  environment: string
  accesses: number
}

export interface AccessStats {
  period: string
  top_users: AccessStatsTopUser[]
  deny_reasons: AccessStatsDeny[]
  top_apps: AccessStatsTopApp[]
}

export interface CertExpiryItem {
  application_id: number
  slug: string
  hostname: string
  cert_arn: string
  expires_at: string | null
  days_remaining: number | null
}

export interface OnboardingHistoryItem {
  job_id: number
  slug: string
  tenant_id: number
  environment: string
  status: JobStatus
  user_id: number
  user_email: string | null
  created_at: string
  finished_at: string | null
  duration_seconds: number | null
}

export interface ProbeResponse {
  application_id: number
  ok: boolean
  http_status: number | null
  latency_ms: number | null
  error: string | null
  probed_at: string
}

/**
 * Timeline de auditoria por app (Lote 8 — Sub-projeto 3).
 * Espelha ``ztna_onboarding.schemas.AppAuditEventItem``.
 */
export interface AppAuditEventItem {
  id: number
  event_type: string | null
  action: string | null
  actor_user_id: number | null
  actor_user_email: string | null
  actor_user_name: string | null
  severity: string | null
  outcome: string | null
  payload: unknown
  created_at: string
}

export interface AppAuditEventsResponse {
  items: AppAuditEventItem[]
  next_cursor: string | null
}

/**
 * Resposta do POST /admin/ztna/apps/{id}/toggle (Lote F1 — Sub-projeto 5).
 * Espelha ``ztna_onboarding.schemas.AppToggleResponse``.
 */
export interface AppToggleResponse {
  application_id: number
  enabled: boolean
  previous_enabled: boolean
  /**
   * Preenchido quando a acao foi para aprovacao em vez de aplicada.
   * A acao NAO aconteceu — os demais campos refletem o estado ATUAL, nao
   * o solicitado. Espelha o campo homonimo do schema do backend.
   */
  gmud_id?: number | null
}

export type AppAccessMode = 'enforced' | 'paused' | 'open'

/**
 * Resposta do POST /admin/ztna/apps/{id}/access-mode.
 * Espelha ``ztna_onboarding.schemas.AppAccessModeResponse``.
 */
export interface AppAccessModeResponse {
  application_id: number
  mode: AppAccessMode
  previous_mode: AppAccessMode
  mechanism: string | null
  job_id: number | null
  /**
   * Preenchido quando a acao foi para aprovacao em vez de aplicada.
   * A acao NAO aconteceu — os demais campos refletem o estado ATUAL, nao
   * o solicitado. Espelha o campo homonimo do schema do backend.
   */
  gmud_id?: number | null
}

/**
 * Body do PATCH /admin/ztna/apps/{id} (Lote F2 — Sub-projeto 5).
 * Todos os campos opcionais; envio vazio retorna 400 empty_patch.
 */
export interface AppUpdateRequest {
  display_name?: string
  upstream_url?: string
  access_policy?: Record<string, unknown>
  identity_mode?: 'header' | 'oidc' | 'mtls'
  oidc_redirect_uris?: string[]
  /** Hostname publico (FQDN). Editar requer DNS Route53 + cert ACM cobrindo o novo nome. */
  hostname?: string
  /** Quando true, apenas funcionários da organização dona do app acessam. Default false = público. */
  is_private?: boolean
}

/** Resposta do PATCH com campos efetivamente alterados. */
export interface AppUpdateResponse {
  application_id: number
  fields_changed: string[]
  /**
   * Preenchido quando a acao foi para aprovacao em vez de aplicada.
   * A acao NAO aconteceu — os demais campos refletem o estado ATUAL, nao
   * o solicitado. Espelha o campo homonimo do schema do backend.
   */
  gmud_id?: number | null
}

/**
 * Body do POST /admin/ztna/apps/{id}/deprovision (Lote F3 — Sub-projeto 5).
 * mode='full' eh irreversivel; mode='dns_only' preserva app row pra re-import.
 * confirm_hostname deve bater exato com app.hostname (defesa UX).
 */
export interface AppDeprovisionRequest {
  mode: 'dns_only' | 'full'
  confirm_hostname: string
}

// ─────────────────────────────────────────────────────────────────
// Derived UI types
// ─────────────────────────────────────────────────────────────────

/** Discrete health bucket derived from AppHealthSummary. */
export type HealthBucket = 'healthy' | 'degraded' | 'failed' | 'idle'

export function deriveHealthBucket(app: AppHealthSummary): HealthBucket {
  if (app.last_probed_at == null) return 'idle'
  if (app.last_ok === true && (app.last_latency_ms ?? 0) < 800) return 'healthy'
  if (app.last_ok === true) return 'degraded'
  return 'failed'
}

export interface FilterState {
  search: string
  envs: string[]
  statuses: HealthBucket[]
  /** Quando true, dashboard inclui apps com enabled=false (suspensas). */
  includeDisabled?: boolean
}

// ─────────────────────────────────────────────────────────────────
// Drift Alerts (Gap 4 — 2026-05-10)
// ─────────────────────────────────────────────────────────────────

/**
 * Linha da tabela ztna_drift_alerts.
 * Espelha ``ztna_onboarding.schemas.DriftAlertItem``.
 */
export interface DriftAlert {
  id: number
  application_id: number | null
  job_id: number | null
  drift_type: string
  resource_type: string | null
  resource_arn: string | null
  detected_at: string
  resolved_at: string | null
  error: string | null
}

/**
 * Resposta paginada do GET /admin/ztna/drift-alerts.
 * Espelha ``ztna_onboarding.schemas.DriftAlertsResponse``.
 */
export interface DriftAlertsResponse {
  items: DriftAlert[]
  total: number
  open_count: number
  resolved_count: number
}


/** O que a adoção FARIA com uma app, sem fazer.
 *
 *  `estado` decide o que a tela oferece — e três dos sete estados não oferecem
 *  ação nenhuma. `origem_dos_campos` diz, campo a campo, se o valor veio de
 *  `applications` (`coluna`), do Route53 da conta do tenant (`descoberta`) ou
 *  de derivação (`derivado`): é o que o operador confere antes de autorizar uma
 *  escrita em DNS.
 */
export interface AdoptionPreview {
  application_id: number
  hostname: string
  estado:
    | 'ja_no_ztna'
    | 'servido_pela_plataforma'
    | 'no_broker_antigo'
    | 'na_origem'
    | 'nao_medido'
    | 'registro_ausente'
    | 'zona_indecidivel'
  motivo: string | null
  zone: HostedZoneItem | null
  record_atual: Record<string, unknown> | null
  alvo_atual: string | null
  payload_derivado: Record<string, unknown> | null
  origem_dos_campos: Record<string, string>
  tem_historico: boolean
  medido_em: string | null
  intercepted: boolean | null
}

export interface AdoptResult {
  application_id: number
  /** `execucao` criou job de provision; `registro` gravou o histórico medido. */
  mode: 'execucao' | 'registro'
  estado: string
  job_id: number | null
  status: string | null
  gmud_id: number | null
}

export interface DiscardResult {
  application_id: number
  hostname: string
  /** O que o descarte NÃO tocou: `dns`, `cert`, `alb_rule`. A tela repete item
   *  a item — a app some da lista e o hostname continua respondendo. */
  nao_tocado: string[]
}
